import { useMemo, useRef } from "react";
import { ArtButton, ArtTable, ArtTableHandle, HeadCell, SSRTableBuilder, useApiService } from "@art/shared-ui";
import { Box } from "@mui/material";
import {
    ClientRoleOption,
    normalizeClientRoles,
    roleSelectionPage,
} from "./role-selection";



export default function ArtClientRoleAssignDialog({ data, closeDialog, masterRoles }: any) {
    const tableRef = useRef<ArtTableHandle>(null);
    const apiService = useApiService();

    const roles = useMemo(
        () => normalizeClientRoles(masterRoles),
        [masterRoles],
    );
    const selectedRoleIds = useMemo(
        () => data?.service_user?.roles.map((role: any) => String(role.id)) ?? [],
        [data?.service_user?.roles],
    );



    const headCells: HeadCell<ClientRoleOption>[] = [

        {
            id: 'name',
            numeric: false,
            disablePadding: false,
            label: 'Role',
            getColData: (row: any) => row["name"],
        },

        {
            id: 'description',
            numeric: false,
            disablePadding: false,
            label: 'Description',
            getColData: (row: any) => row["description"],
        },


    ];




    const tableBuilder = useMemo(
        () => SSRTableBuilder<ClientRoleOption>({
            endpoint: "skip",
            onResponse: (params) => roleSelectionPage(roles, params),
        }),
        [roles],
    );


    const handleUpdateRoles = async (selected: string[]) => {
        const newAddedRoles = selected
            .filter((id: string) => !selectedRoleIds.includes(id))
            .map((id: string): { id: number; name: string } | null => {
                const role = roles.find((candidate) => candidate.id === String(id));
                return role ? { id: Number(role.id), name: role.name } : null;
            })
            .filter((role: { id: number; name: string } | null): role is { id: number; name: string } => role !== null);

        const removedRoles = selectedRoleIds
            .filter((id: string) => !selected.includes(id))
            .map((id: string): { id: number; name: string } | null => {
                const role = roles.find((candidate) => candidate.id === String(id));
                return role ? { id: Number(role.id), name: role.name } : null;
            })
            .filter((role: { id: number; name: string } | null): role is { id: number; name: string } => role !== null);
        try {
            const requests: Promise<any>[] = [];

            if (newAddedRoles.length > 0) {
                requests.push(
                    apiService.post(`/client/${data.client_id}/configuration/roles`, {
                        role_ids: newAddedRoles,
                    })
                );
            }

            if (removedRoles.length > 0) {
                requests.push(
                    apiService.delete(`/client/${data.client_id}/configuration/roles`, {
                        data: { role_ids: removedRoles },
                    })
                );
            }

            await Promise.all(requests);
            closeDialog();
            tableRef.current?.refresh?.();
        } catch (error) {
            console.error("Error updating roles:", error);
        }
    };




    return (
    <Box className="non-clickable-table ">
        <ArtTable ref={tableRef} headCells={headCells}
            builder={tableBuilder}
            search={{ enabled: true, value: "" }}
            disableEmptyRows
            pagination={{ initialPage: 0 }}
            bulkOperationActions={{
                selected: selectedRoleIds,
                findDiff: (selected) => {
                    const newAddedIds = selected.filter((id: any) => !selectedRoleIds.includes(id));
                    const removedIds = selectedRoleIds.filter((id: any) => !selected.includes(id));
                    return {
                        added: newAddedIds.length,
                        removed: removedIds.length
                    }
                },
                actions: (selected) => {
                    const newAddedIds = selected.filter((id: any) => !selectedRoleIds.includes(id));
                    const removedIds = selectedRoleIds.filter((id: any) => !selected.includes(id));
                    if (newAddedIds.length === 0 && removedIds.length === 0) {
                        return [];
                    }
                    return [
                        <ArtButton dataQa="btn-update" label="Update" onClick={() => {
                            handleUpdateRoles(selected)
                        }} />
                    ]
                }
            }}
        />
    </Box>

    );
}
